import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Toaster } from "sonner";
import Seo, { makeBreadcrumb } from "@/components/seo/Seo";
import RelatedContentCarousel from "@/components/RelatedContentCarousel";
import SolutionStats from "./SolutionStats";
import SolutionWorkflow from "./SolutionWorkflow";
import DecorationMethods from "./DecorationMethods";
import WhyChoose from "./WhyChoose";
import StickyLeadForm from "@/components/StickyLeadForm";

export default function SolutionDetailPage({
  title, tagline, icon: Icon, image, sections, seo, parent, relatedTag, relatedTitle,
  stats, workflow, decoration_methods, why_choose, cta_label, final_cta,
}) {
  // parent: { name, path }. Defaults to Solutions.
  const parentInfo = parent || { name: "Solutions", path: "/solutions" };
  return (
    <div className="page-transition">
      {seo && (
        <Seo
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          path={seo.path}
          jsonLd={
            seo.path
              ? makeBreadcrumb([
                  { name: "Home", path: "/" },
                  { name: parentInfo.name, path: parentInfo.path },
                  { name: title, path: seo.path },
                ])
              : undefined
          }
        />
      )}
      <Toaster position="top-right" richColors />

      {/* Hero - Full Width */}
      <section className="pt-28 pb-8 lg:pt-36 lg:pb-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to={parentInfo.path} className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 mb-6">
              <ArrowLeft className="h-3.5 w-3.5" /> All {parentInfo.name}
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">{title}</h1>
            <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed">{tagline}</p>
            {image && (
              <div className="mt-8 rounded-xl overflow-hidden shadow-lg border border-slate-200">
                <img src={image} alt={title} className="w-full h-56 lg:h-72 object-cover" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Optional Stats section (right after hero) */}
      <SolutionStats stats={stats} />

      {/* Optional Decoration Methods badge grid */}
      <DecorationMethods methods={decoration_methods} />

      {/* Optional Workflow diagram */}
      <SolutionWorkflow workflow={workflow} />

      {/* Main Content + Sticky Sidebar */}
      <section className="pb-12 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left - Content */}
            <div className="lg:col-span-3 space-y-8">
              {sections.map((section, i) => (
                <motion.div
                  key={section.heading}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className={`rounded-xl border border-slate-200 p-6 lg:p-8 ${i % 2 === 0 ? "bg-slate-50/60" : "bg-white"}`}
                >
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 mb-6">{section.heading}</h2>
                  <div className="space-y-4">
                    {section.points.map((point) => (
                      <div key={point.title} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{point.title}</p>
                          <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{point.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Right - Sticky Lead Form */}
            <div className="lg:col-span-2 relative">
              <div className="lg:sticky lg:top-24">
                <StickyLeadForm title={title} ctaLabel={cta_label} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optional "Why X Choose Antera" differentiator section */}
      <WhyChoose whyChoose={why_choose} />

      {/* Bottom CTA */}
      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            {final_cta?.heading || "See How Antera Can Transform Your Business"}
          </h2>
          <p className="mt-3 text-sm text-sky-100">
            {final_cta?.subheading || "A personalized demo built around your workflows, your suppliers, and your business."}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
                {final_cta?.button_label || "Request a Demo"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {relatedTag && (
        <RelatedContentCarousel
          tag={relatedTag}
          title={relatedTitle || `Latest on ${relatedTag}`}
          testId={`solution-related-${(relatedTag || "").toLowerCase().replace(/\s+/g, "-")}`}
        />
      )}

      {/* Mobile-only form (shown below content on mobile) */}
      <section className="lg:hidden py-12 bg-slate-50">
        <div className="max-w-md mx-auto px-4">
          <StickyLeadForm title={title} variant="mobile" ctaLabel={cta_label} />
        </div>
      </section>
    </div>
  );
}
